// tenancyTypes.ts
import { Timestamp } from 'firebase/firestore';

/**
 * Payment status for a specific rent period
 */
export type PaymentStatus = 'pending' | 'paid' | 'overdue' | 'partially-paid';

// New Interface for monthly snapshots
export interface BillingPeriodSnapshot {
  month: number;
  year: number;
  rentAmount: number;
  amountPaid: number;
  paymentStatus: PaymentStatus;
  isClosed: boolean; // true when the month is over
}

export interface Tenancy {
  id?: string;
  landlordId: string;
  tenantId: string | null;
  listingId: number;

  // Configuration
  rentAmount: number;
  rentDueDay: number;
  depositAmount?: number;

  // Dates
  leaseStartDate: Timestamp;
  leaseEndDate?: Timestamp;

  // Compliance
  rtbRegisteredOn?: Timestamp;

  arrears: number; // Accumulated unpaid rent from previous months

  // Current Active Period
  currentPeriod: {
    month: number;
    year: number;
    paymentStatus: PaymentStatus;
    amountPaid: number;
    datePaid?: Timestamp;
    lastReminderSent?: Timestamp;
    reminderCount: number;
  };

  // Transaction Log (Existing)
  paymentHistory?: {
    id: string;
    date: Timestamp;
    amount: number;
    type: 'rent' | 'arrears' | 'deposit';
    method: 'cash' | 'bank-transfer' | 'other';
    notes?: string;
    recordedBy: string;
  }[];

  // Monthly Snapshots (NEW - History of every month's final status)
  billingHistory?: BillingPeriodSnapshot[];

  // Metadata
  tenantInfo: { name: string; email: string; phone?: string };
  propertyInfo: { title: string; address: string };
  createdAt: Timestamp;
  lastUpdated: Timestamp;
  isActive: boolean;
  notes?: string;
  subTenants?: {
    id: string;
    name: string;
    email: string;
    phone?: string;
    userId?: string | null;
    status: 'active' | 'removed';
    addedAt: Timestamp;
    removedAt?: Timestamp | null;
    invitedBy: string; // landlord user id who added
  }[];
}

/**
 * Payment record for historical tracking
 */
export interface PaymentRecord {
  id?: string;
  month: number;
  year: number;
  amountPaid: number;
  paymentStatus: PaymentStatus;
  datePaid?: Timestamp;
  paymentMethod?: 'cash' | 'bank-transfer' | 'other';
  notes?: string;
}

/**
 * Historical record of a rent period
 */
export interface PeriodHistoryEntry {
  month: number;
  year: number;
  paymentStatus: PaymentStatus;
  amountPaid: number;
  datePaid: Timestamp | null;
  rentAmount: number; // Rent amount for that period
  balance: number; // Unpaid balance at end of period
}

/**
 * DTO for creating a new tenancy
 */
export interface CreateTenancyDTO {
  landlordId: string;
  tenantId: string;
  listingId: number;
  rentAmount: number;
  rentDueDay: number;
  depositAmount?: number;
  leaseStartDate: Date | Timestamp;
  leaseEndDate?: Date | Timestamp;
  tenantInfo: {
    name: string;
    email: string;
    phone?: string;
  };
  propertyInfo: {
    title: string;
    address: string;
  };
  notes?: string;
}

/**
 * DTO for marking rent as paid
 */
export interface MarkRentPaidDTO {
  tenancyId: string;
  amountPaid: number;
  paymentMethod?: 'cash' | 'bank-transfer' | 'other';
  notes?: string;
}

export interface Tenant {
  id: string;
  name: string;
  email: string;
  phone?: string;
  moveInDate: string;
  leaseEndDate?: string;
  rentAmount?: number;
  depositAmount?: number;
  status: 'active' | 'inactive';
  rtbRegisteredOn?: { _seconds: number; _nanoseconds: number };
  paymentStatus?: 'current' | 'late' | 'upcoming' | 'paid';
  isManual?: boolean;
  rentDueDay?: number;
  subTenants?: Array<{
    id?: string;
    name: string;
    email: string;
    phone?: string;
  }>;
}

/**
 * Rent reminder email data
 */
export interface RentReminderData {
  tenancy: Tenancy;
  daysOverdue: number;
  reminderType: 'initial' | 'follow-up' | 'final';
}

/**
 * Type guard to check if payment is overdue
 */
export function isPaymentOverdue(tenancy: Tenancy): boolean {
  const now = new Date();
  const currentMonth = now.getMonth() + 1; // 1-12
  const currentYear = now.getFullYear();
  const currentDay = now.getDate();

  // Check if we're in the same month and past due date
  if (
    tenancy.currentPeriod.month === currentMonth &&
    tenancy.currentPeriod.year === currentYear &&
    currentDay > tenancy.rentDueDay &&
    tenancy.currentPeriod.paymentStatus !== 'paid'
  ) {
    return true;
  }

  // Check if we're in a future month and payment is not paid
  if (
    (tenancy.currentPeriod.year < currentYear ||
      (tenancy.currentPeriod.year === currentYear &&
        tenancy.currentPeriod.month < currentMonth)) &&
    tenancy.currentPeriod.paymentStatus !== 'paid'
  ) {
    return true;
  }

  return false;
}

/**
 * Calculate days overdue for a tenancy
 */
export function getDaysOverdue(tenancy: Tenancy): number {
  const now = new Date();
  const dueDate = new Date(
    tenancy.currentPeriod.year,
    tenancy.currentPeriod.month - 1,
    tenancy.rentDueDay
  );

  if (now <= dueDate) {
    return 0;
  }

  const diffTime = now.getTime() - dueDate.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
}

/**
 * Get reminder type based on days overdue
 */
export function getReminderType(
  daysOverdue: number,
  reminderCount: number
): 'initial' | 'follow-up' | 'final' {
  if (reminderCount === 0 || daysOverdue <= 3) {
    return 'initial';
  } else if (daysOverdue <= 7) {
    return 'follow-up';
  } else {
    return 'final';
  }
}
